import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, combineLatest, from, take } from "rxjs";
import { IRestLayerRequest } from "@gtmhub/core";
import { CurrentEmployeeActions, ICurrentEmployeeStoreState } from "@gtmhub/employees";
import { getActiveSessionsMap, getEmployeeTeamIds, getGoalsToApprove, getNotUpdatedMetricsInCurrentCadence } from "@gtmhub/home/utils";
import { GoalsChangedEventArgs, OKRsEventType } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { filterActiveSessions } from "@gtmhub/sessions/redux/session-selectors";
import { PlanningSessionService } from "@gtmhub/sessions/services/planning-session.service";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { TaskService } from "@gtmhub/tasks/services/task.service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { FeatureModuleService } from "@webapp/feature-toggles/services/feature-module.service";

const userWorkflowItemsFields = "hubWorkflowItems{goalsToReview{id,name,currentUserApproved},itemsToReview,actionsNeededToReview,actionsNeededOwned}";

@Injectable()
export class TodoActionsService {
  private $actionsCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private notUpdatedMetricsIdsInCurrentCadence: string[];
  private actionsInitialized = false;

  constructor(
    private broadcastService: BroadcastService,
    private metricService: MetricService,
    private taskService: TaskService,
    private planningSessionService: PlanningSessionService,
    private featureModuleService: FeatureModuleService,
    private currentEmployeeActions: CurrentEmployeeActions,
    private sessionsActions: PlanningSessionsActions
  ) {
    this.broadcastService.on("approvedGoal").subscribe(() => {
      this.$actionsCount.next(this.$actionsCount.value - 1);
    });

    this.broadcastService.on("todoTaskAdded").subscribe(() => {
      this.$actionsCount.next(this.$actionsCount.value + 1);
    });

    this.broadcastService.on("todoTaskDeleted").subscribe(() => {
      this.$actionsCount.next(this.$actionsCount.value - 1);
    });

    this.broadcastService.on<GoalsChangedEventArgs>(OKRsEventType.OKRS_GOALS_CHANGED).subscribe((args): void => {
      if (args.reason === "metricUpdated" && this.notUpdatedMetricsIdsInCurrentCadence.includes(args.detail.metric.id)) {
        this.$actionsCount.next(this.$actionsCount.value - 1);
        this.notUpdatedMetricsIdsInCurrentCadence = this.notUpdatedMetricsIdsInCurrentCadence.filter((id) => id !== args.detail.metric.id);
      }
    });

    const { reduxStore } = reduxStoreContainer;
    reduxStore.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());
    reduxStore.dispatch(this.sessionsActions.getSessionsIfMissing());

    const reduxObserver = new ReduxStoreObserver(reduxStore);
    reduxObserver.whenFetched$<ICurrentEmployeeStoreState & ISessionsStoreState>("currentEmployee", "sessions").subscribe((state) => {
      this.initToDoActions(state);
    });
  }

  private initToDoActions(state: ISessionsStoreState & ICurrentEmployeeStoreState): void {
    if (this.actionsInitialized) return;
    this.actionsInitialized = true;
    const employee = state.currentEmployee.info;
    const sessions = state.sessions.items;

    // todo: remove activeSessionIds when there's proper fix for OKRs from archived sessions
    const employeeIdAndTeamIds = getEmployeeTeamIds(employee);
    const activeSessions = filterActiveSessions(sessions);
    const activeSessionIds = activeSessions.map((s) => s.id);
    const mongoTaskFilter: IRestLayerRequest = {
      filter: {
        $and: [
          { ownerId: employee.id },
          { status: { $in: ["inprogress", "todo"] } },
          {
            $or: [{ sessionId: { $exists: false } }, { sessionId: { $in: activeSessionIds } }],
          },
          { $or: [{ archived: { $exists: false } }, { archived: false }] },
        ],
      },
      fields: "dueDate,name,parentId",
    };

    const mongoMetricsFilter: IRestLayerRequest = {
      filter: {
        $and: [{ ownerIds: { $in: employeeIdAndTeamIds } }, { sessionId: { $in: activeSessionIds } }],
      },
      // *****lastCheckinDate must go with latestSnapshotAt otherwise the endpoint doesn't return the field*****
      fields: "id,goalId,name,sessionId,ownerIds,dueDate,attainment,manualType,lastCheckInDate,latestSnapshotAt",
    };
    combineLatest({
      metrics: from(this.metricService.getMetricsV2(mongoMetricsFilter)),
      tasks: from(this.taskService.getTasksV2(mongoTaskFilter)),
      workflowItems: from(this.planningSessionService.getCurrentUserWorkflowItems(userWorkflowItemsFields)),
      tasksEnabled: this.featureModuleService.isTaskModuleEnabled$().pipe(take(1)),
    }).subscribe((map) => {
      const activeSessionsMap = getActiveSessionsMap(activeSessions);
      const metricsNotUpdated = getNotUpdatedMetricsInCurrentCadence(map.metrics.items, activeSessionsMap, employee);
      const goalsNotApproved = getGoalsToApprove(map.workflowItems.hubWorkflowItems, sessions);

      const tasksCount = map.tasksEnabled ? map.tasks.totalCount || 0 : 0;
      this.$actionsCount.next((goalsNotApproved.length || 0) + (metricsNotUpdated.length || 0) + tasksCount);
      this.notUpdatedMetricsIdsInCurrentCadence = metricsNotUpdated.map((metric) => metric.id);
    });
  }

  public actionsCount$(): Observable<number> {
    return this.$actionsCount.asObservable();
  }
}
